import { useMemo } from 'react';

interface TimeWindowInputProps {
  value: number;
  onChange: (minutes: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  label?: string;
}

interface PresetButton {
  label: string;
  minutes: number;
}

const PRESETS: PresetButton[] = [
  { label: '5min', minutes: 5 },
  { label: '15min', minutes: 15 },
  { label: '30min', minutes: 30 },
  { label: '1hr', minutes: 60 },
  { label: '4hr', minutes: 240 },
  { label: '24hr', minutes: 1440 },
];

function formatDuration(minutes: number): string {
  if (minutes <= 0) return '0 minutes';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (remainingMinutes > 0) {
    parts.push(`${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`);
  }

  return parts.join(' ');
}

export function TimeWindowInput({
  value,
  onChange,
  min = 0,
  max = 1440,
  disabled = false,
  label,
}: TimeWindowInputProps) {
  const humanLabel = useMemo(() => formatDuration(value), [value]);

  const clamp = (v: number): number => Math.max(min, Math.min(max, v));

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(clamp(parseInt(e.target.value, 10)));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    if (Number.isFinite(parsed)) {
      onChange(clamp(parsed));
    }
  };

  const handlePresetClick = (minutes: number) => {
    onChange(clamp(minutes));
  };

  // Only show presets that fall within the allowed range
  const visiblePresets = PRESETS.filter((p) => p.minutes >= min && p.minutes <= max);

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      {label && (
        <legend className="label">{label}</legend>
      )}

      {/* Slider + number input row */}
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={handleSliderChange}
          className="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700 accent-blue-500"
        />
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={min}
            max={max}
            value={value}
            onChange={handleNumberChange}
            className="input w-20 text-center tabular-nums"
          />
          <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
        </div>
      </div>

      {/* Human-readable label */}
      <p className="text-xs text-gray-500 dark:text-gray-400">{humanLabel}</p>

      {/* Preset buttons */}
      {visiblePresets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {visiblePresets.map((preset) => {
            const isActive = value === preset.minutes;
            return (
              <button
                key={preset.minutes}
                type="button"
                onClick={() => handlePresetClick(preset.minutes)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}
